import { LionIcon } from "@/components/ContactInterest";
import {useLiquidGold} from './mobile/LiquidGoldContext';
import LiquidShell from './mobile/LiquidShell';
import OpportunityScene, { isOpportunityScene } from './opportunities/OpportunityScene';
import {useMobileDesign} from './mobile/MobileDesign';
import ReferenceShell from './reference/ReferenceShell';
import {useReferencePreview} from '@/lib/referencePreview';
import { ADMIN_MENU, SHARED_WORK_MENU, OWNER_MENU } from "@/lib/adminNavigation";
import WhatsAppAccess from '@/components/WhatsAppAccess';
import { GlobalXPIndicator } from '@/components/RewardFX';
import DesktopCockpitHeader from '@/components/DesktopCockpitHeader';
import { useSafeBack } from '@/lib/useSafeBack';
import { canNavigate, canPrewarm, homeFor } from '@/lib/navigation';
import AreaNavLink from "@/components/AreaNavLink";
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Mail, Home, Phone, Briefcase, BarChart3, Trophy, User, Bell, LayoutDashboard, Users, Building2, Menu, LogOut, ChevronLeft, UserCheck, Megaphone, Eye, EyeOff, Star, History, TrendingUp, RotateCcw, CalendarClock, ScrollText, ShieldCheck } from "lucide-react";
import "@/components/Navigation.css";
import "@/components/LuxuryCockpit.css";
import MobileSalesConsole from "@/components/MobileSalesConsole";
import "@/components/MobilePremium.css";
import "@/components/DesktopPremium.css";
import { CareerCompact } from "@/components/Gamification";
import { LogoRow } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { useShellStatus } from "@/context/ShellStatusContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { cx } from "@/components/common";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";

export function UndoButton() {
  const nav = useNavigate();
  const { undo: info, refresh } = useShellStatus();
  const [busy, setBusy] = useState(false);
  const doUndo = async () => {
    if (!info.available || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/undo");
      toast.success(`„${data.label || "Aktion"}“ rückgängig gemacht.`);
      await refresh();
      if (data.redirect) nav(data.redirect);
    } catch (e) { toast.error(e?.response?.data?.detail || "Rückgängig nicht möglich."); }
    finally { setBusy(false); }
  };
  return (
    <button onClick={doUndo} disabled={!info.available || busy} data-testid="global-undo"
      title={info.available ? `Rückgängig: ${info.label}` : "Nichts zum Rückgängigmachen"}
      className={cx("relative w-10 h-10 rounded-xl border flex items-center justify-center transition",
        info.available ? "border-amber-500/40 text-amber-300 hover:bg-amber-500/10" : "border-white/10 text-slate-600 cursor-not-allowed")}>
      <RotateCcw className={cx("w-5 h-5", busy && "animate-spin")} />
    </button>
  );
}

export function ContactVisibilityToggle() {
  const { showContactData, setShowContactData } = useAuth();
  const on = showContactData !== false;
  return (
    <button onClick={() => setShowContactData(!on)} data-testid="contact-visibility-toggle"
      title={on ? "Kontaktdaten ausblenden" : "Kontaktdaten anzeigen"}
      className={cx("relative w-10 h-10 rounded-xl border flex items-center justify-center transition",
        on ? "border-amber-500/20 text-amber-300 hover:bg-amber-500/10" : "border-white/10 text-slate-500 hover:bg-white/5")}>
      {on ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
    </button>
  );
}

function useUnread() { return useShellStatus().notifications_unread; }

function useReferralOpen() { return useShellStatus().referrals_open; }

const MOBILE_NAV = [
  { to: "/m/opportunities", label: "Chancen", icon: Briefcase, area: "opportunities", mobile: true },
  { to: "/m/call", label: "Cold", icon: Phone, area: "cold_call", mobile: true },
  { to: "/m/my-contacts", label: "Meine Kontakte", icon: UserCheck, area: "my_contacts", mobile: true },
  { to: "/m/referrals", label: "Empfehlungen", icon: Star, gold: true, area: "referrals", mobile: true },
];
const EMP_NAV = [
  { to: "/m/home", label: "Home", icon: Home, area: "home" },
  ...MOBILE_NAV,
  { to: "/m/promo", label: "Promo", icon: Megaphone, area: "promo" },
  { to: "/m/performance", label: "Analytics", icon: BarChart3, area: "analytics" },
  { to: "/m/profile", label: "Einstellungen", icon: User },
  { to: "/m/career", label: "Meine Karriere", icon: Trophy },
];

const SECONDARY_NAV = [
  ...SHARED_WORK_MENU,
  ...OWNER_MENU,
  { to: "/m/home", label: "Home", icon: Home, area: "home" },
  { to: "/m/call", label: "Cold Call", icon: Phone, area: "cold_call" },
  { to: "/m/my-contacts", label: "Meine Kontakte", icon: UserCheck, area: "my_contacts" },
  { to: "/m/opportunities", label: "Chancen", icon: Briefcase, area: "opportunities" },
  { to: "/admin/dashboard", label: "Admin-Bereich", icon: LayoutDashboard, adminOnly: true },
  { to: "/m/career", label: "Meine Karriere", icon: Trophy },
  { to: "/m/referrals", label: "Empfehlungen", icon: Star, area: "referrals" },
  { to: "/m/ranking", label: "Ranglisten", icon: BarChart3 },
  { to: "/m/appointments", label: "Termine", icon: CalendarClock },
  { to: "/m/promo", label: "Promo", icon: Megaphone, area: "promo" },
  { to: "/m/performance", label: "Analytics", icon: BarChart3, area: "analytics" },
  { to: "/team/cockpit", label: "Mein Team", icon: Users, teamOnly: true },
  { to: "/team/contracts", label: "Abschlüsse", icon: ScrollText, teamOnly: true },
  { to: "/team/promoter-assignments", label: "Promoter-Zuordnungen", icon: UserCheck, teamOnly: true },
  { to: "/m/audit-log", label: "Audit-Log", icon: ScrollText, adminOnly: true },
  { to: "/m/login-log", label: "Login-Log", icon: ShieldCheck, adminOnly: true },
  { to: "/m/profile", label: "Einstellungen", icon: User },
];

function BrandHome({ onClick, compact = false, to = "/m/home" }) {
  const { user } = useAuth();
  const target = to === "/m/home" ? homeFor(user) : to;
  return <AreaNavLink to={target} onClick={onClick} aria-label="Löwenstark – zur Startseite" className="shrink-0 rounded focus-visible:outline focus-visible:outline-amber-300"><LogoRow compact={compact} /></AreaNavLink>;
}

function DesktopHomeActions({ to = "/m/home" }) {
  const { logout } = useAuth();
  return <div className="hidden lg:flex items-center gap-2" data-testid="desktop-home-actions">
    <AreaNavLink to={to} className="flex items-center gap-2 rounded-xl border border-amber-500/20 px-3 h-10 text-sm text-amber-300"><Home className="w-4 h-4" />Home</AreaNavLink>
    <button onClick={logout} className="flex items-center gap-2 rounded-xl border border-amber-500/20 px-3 h-10 text-sm text-amber-300" data-testid="desktop-logout"><LogOut className="w-4 h-4" />Logout</button>
  </div>;
}

function LuxuryHeaderNav() {
 const links = [["/m/home","Home"],["/m/tasks","Aufgaben"],["/m/my-contacts","Kontakte"],["/m/referrals","Empfehlungen"],["/m/appointments","Kalender"],["/m/performance","Analytics"]];
 return <nav className="luxury-header-nav" aria-label="Arbeitsbereiche">{links.map(([to,label])=><AreaNavLink key={to} to={to} className={({isActive})=>isActive?"is-active":""}>{label}</AreaNavLink>)}</nav>;
}

function EmployeeMenu() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const isAdminLogViewer = user?.role === "admin";
  const items = SECONDARY_NAV.filter(item => (!item.prewarmOnly || canPrewarm(user)) && (!item.janOnly || (user?.email||" ").trim().toLowerCase()==="jan.nesic@loewen-stark.net") && (!item.teamOnly || user?.role === "teamleiter") && (!item.adminOnly || user?.role === "admin") && (!item.adminOnly || isAdminLogViewer));
  return <Sheet open={open} onOpenChange={setOpen}>
    <SheetTrigger asChild><button aria-label="Menü öffnen" data-testid="employee-menu-toggle" className="w-10 h-10 shrink-0 rounded-xl border border-amber-500/20 flex items-center justify-center text-amber-300"><Menu className="w-5 h-5" /></button></SheetTrigger>
    <SheetContent side="right" className="w-80 max-w-full bg-[#070B16] border-amber-500/15 flex flex-col" aria-describedby={undefined}>
      <SheetTitle>Menü</SheetTitle>
      {user?.role === "admin" ? <SidebarContent onNav={() => setOpen(false)} /> : <><nav className="flex-1 overflow-y-auto space-y-1">
        <WhatsAppAccess label onClick={() => setOpen(false)} />
        {items.map(item => <AreaNavLink key={item.to} to={item.to} onClick={() => setOpen(false)} className={({ isActive }) => cx("flex items-center gap-3 rounded-xl px-3 py-3 text-sm", isActive ? "bg-amber-500/15 text-amber-200" : "text-slate-300 hover:bg-white/5")}><item.icon className="w-5 h-5" />{item.label}</AreaNavLink>)}
      </nav>
      <div className="flex gap-2 border-t border-white/10 pt-3"><UndoButton /><ContactVisibilityToggle /></div>

      <button onClick={logout} className="flex items-center gap-3 rounded-xl px-3 py-3 text-sm text-slate-300"><LogOut className="w-5 h-5" />Logout</button></>}
    </SheetContent>
  </Sheet>;
}

export function EmployeeShell({ children }) {
  const reference = useReferencePreview();
  const mobileDesign = useMobileDesign();
  const liquid = useLiquidGold();
  const nav = useNavigate();
  const loc = useLocation();
  const unread = useUnread();
  const refOpen = useReferralOpen();
  const { user } = useAuth();
  const baseItems = user?.role === "teamleiter" ? [...EMP_NAV, { to: "/team/cockpit", label: "Mein Team", icon: Users }] : EMP_NAV;
  const items = baseItems;
  const back = useSafeBack(user);
  const permitted = canNavigate(user,loc.pathname + loc.search);
  useEffect(() => { if (!permitted) nav(homeFor(user), { replace:true }); }, [permitted,user,nav]);
  if (!permitted) return null;
  if(liquid)return <LiquidShell unread={unread} tools={<><UndoButton/><ContactVisibilityToggle/></>}>{children}</LiquidShell>;
  if (isOpportunityScene(loc) || (mobileDesign && !["/m/home", "/m/career"].includes(loc.pathname))) return <OpportunityScene unread={unread} tools={<><UndoButton/><ContactVisibilityToggle/></>}>{children}</OpportunityScene>;
  if (reference || mobileDesign) return <ReferenceShell unread={unread} tools={<><UndoButton/><ContactVisibilityToggle/></>}>{children}</ReferenceShell>;
  return (
    <div className={cx("app-bg mobile-premium desktop-premium cockpit-shell app-mobile-console pb-24 min-h-screen", "luxury-cockpit", loc.pathname === "/m/home" && "chrono-shell", loc.pathname === "/m/career" && "career-shell", loc.pathname !== "/m/home" && "console-compact")}>
      <DesktopCockpitHeader unread={unread} tools={<><UndoButton /><ContactVisibilityToggle /></>} />
      <header style={{ top: "var(--employee-preview-height, 0px)" }} className="cockpit-legacy-header sticky top-0 z-30 backdrop-blur-md bg-[#050811]/80 border-b border-amber-500/10">
        <div className={cx(loc.pathname === "/m/career" ? "career-full-width" : ["/m/home", "/m/whatsapp"].includes(loc.pathname) ? "max-w-7xl" : "max-w-3xl", "mx-auto px-4 h-14 flex items-center justify-between")}>
          <div className="flex items-center gap-2">
            {loc.pathname !== "/m/home" && (
              <button onClick={back} aria-label="Zurück" data-testid="emp-back" className="w-9 h-9 rounded-xl border border-amber-500/20 flex items-center justify-center text-amber-300 hover:bg-amber-500/10"><ChevronLeft className="w-5 h-5" /></button>
            )}
            <BrandHome compact />
          </div>
          <div className="flex items-center gap-2">
            <LuxuryHeaderNav />
            <div className="hidden lg:flex gap-2"><UndoButton /><ContactVisibilityToggle /></div>
            <button onClick={() => nav("/m/notifications")} aria-label="Benachrichtigungen öffnen" data-testid="notif-bell"
              className="relative w-10 h-10 rounded-xl border border-amber-500/20 flex items-center justify-center text-amber-300 hover:bg-amber-500/10">
              <Bell className="w-5 h-5" />
              {unread > 0 && <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center">{unread}</span>}
            </button>
            <WhatsAppAccess />
            <EmployeeMenu />
          </div>
        </div>
      </header>
      <GlobalXPIndicator />
      <main key={loc.pathname} className={cx(loc.pathname === "/m/career" ? "career-full-width" : ["/m/home", "/m/whatsapp"].includes(loc.pathname) ? "max-w-7xl" : "max-w-3xl", "mx-auto px-4 py-5 page-enter")}>{["/m/home", "/m/my-contacts"].includes(loc.pathname) && <div className={loc.pathname === "/m/home" ? "home-career-compact" : ""}><CareerCompact /></div>}{children}</main>
      <nav aria-label="Hauptnavigation" className="employee-bottom-nav fixed bottom-0 inset-x-0 z-30 backdrop-blur-xl bg-[#030509]/90 border-t border-amber-500/15">
        <div className="max-w-3xl mx-auto grid" style={{ "--desktop-columns": items.length, "--mobile-columns": Math.max(1, items.filter(n => n.mobile).length) }}>
          {items.map((n) => (
            <AreaNavLink key={n.to} to={n.to} data-testid={`emp-nav-${n.label.toLowerCase()}`}
              className={({ isActive }) => cx("flex flex-col items-center gap-1 py-2.5 text-[10px] font-medium transition tap", !n.mobile && "desktop-nav-only", n.gold && "referral-nav-gold",
                isActive ? (n.gold ? "text-amber-300" : "text-amber-300") : (n.gold ? "text-amber-400/90 hover:text-amber-300" : "text-slate-500 hover:text-slate-300"))}>
              {({ isActive }) => (
                <>
                  <span className={cx("relative flex items-center justify-center w-10 h-7 rounded-full transition",
                    n.gold && "ring-1 ring-amber-500/40 bg-amber-500/[0.08] shadow-[0_0_14px_-4px_rgba(212,175,55,0.7)]",
                    isActive && "bg-amber-500/12 shadow-[0_0_16px_-4px_rgba(212,175,55,0.6)]")}>
                    <n.icon className={cx("w-5 h-5", n.gold && "fill-amber-400/20")} />
                    {n.gold && refOpen > 0 && (
                      <span data-testid="emp-nav-referral-count" className="absolute -top-1.5 -right-1.5 min-w-[16px] h-[16px] px-1 rounded-full bg-amber-500 text-[#0a0e17] text-[9px] font-bold flex items-center justify-center">{refOpen}</span>
                    )}
                  </span>
                  <span className="text-center leading-tight px-1">{n.label}</span>
                </>
              )}
            </AreaNavLink>
          ))}
        </div>
      </nav>
      <MobileSalesConsole />
    </div>
  );
}

const TEAM_NAV = [
  { section: "Arbeit & Zahlen", items: SHARED_WORK_MENU },
  { section: "Meine Entwicklung", items: [
    { to: "/m/career", label: "Meine Karriere", icon: Trophy },
    { to: "/m/ranking", label: "Ranglisten", icon: BarChart3 },
    { to: "/m/referrals", label: "Empfehlungen", icon: Star, area: "referrals" },
  ]},
  { section: "Meine Arbeit", items: [
    { to: "/m/home", label: "Start", icon: Home },
    { to: "/m/call", label: "Cold", icon: Phone },
    { to: "/m/my-contacts", label: "Meine Kontakte", icon: UserCheck },
    { to: "/m/leads", label: "Leads", icon: UserCheck },
    { to: "/m/opportunities", label: "Meine Chancen", icon: Briefcase },
    { to: "/m/referrals", label: "Empfehlungen", icon: Star },
  ]},
  { section: "Team führen", items: [
    { to: "/team/cockpit", label: "Team-Cockpit", icon: LayoutDashboard },
    { to: "/team/opportunities", label: "Team-Chancen", icon: TrendingUp },
    { to: "/team/contracts", label: "Abschlüsse", icon: ScrollText },
    { to: "/team/contacts", label: "Team-Kontakte", icon: History },
    { to: "/m/minijob-contacts", label: "Kontakte meiner Minijobber", icon: Users },
    { to: "/team/assigned", label: "Zugewiesene Kontakte", icon: UserCheck },
    { to: "/team/customers", label: "Bestandskunden", icon: Building2 },
    { to: "/team/members", label: "Mitarbeiter", icon: Users },
    { to: "/team/promoter-assignments", label: "Promoter-Zuordnungen", icon: UserCheck },
  ]},
  { section: "Auswertung & Konto", items: [
    { to: "/m/performance", label: "Analytics", icon: BarChart3 },
    { to: "/m/appointments", label: "Termine", icon: CalendarClock },
    { to: "/m/notifications", label: "Benachrichtigungen", icon: Bell, notifications: true },
    { to: "/m/promo", label: "Promo", icon: Megaphone, area: "promo" },
    { to: "/m/profile", label: "Einstellungen", icon: User },
  ]},
];

function TeamSidebarContent({ onNav }) {
  const { user, logout } = useAuth();
  const unread = useUnread();
  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="px-5 py-5 border-b border-amber-500/10 flex items-center justify-between gap-2"><BrandHome onClick={onNav} /><UndoButton /></div>
      <div className="flex-1 overflow-y-auto px-3 py-4 space-y-5">
        {TEAM_NAV.map((section) => (
          <div key={section.section} className={section.section === "Meine Arbeit" ? "hidden lg:block" : undefined}>
            <div className="px-2 mb-2 text-[10px] uppercase tracking-wider text-amber-400/60 font-semibold">{section.section}</div>
            <div className="space-y-0.5">
              {section.items.filter(item => (!item.prewarmOnly || canPrewarm(user)) && (!item.janOnly || (user?.email || "").trim().toLowerCase() === "jan.nesic@loewen-stark.net")).map((item) => (
                <AreaNavLink key={item.to} to={item.to} onClick={onNav}
                  className={({ isActive }) => cx("flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition tap",
                    isActive ? "nav-bar-active bg-amber-500/12 text-amber-200" : "text-slate-400 hover:text-white hover:bg-white/5")}>
                  <item.icon className="w-4 h-4" />{item.label}
                  {item.notifications && unread > 0 && <span className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center">{unread}</span>}
                </AreaNavLink>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="p-3 border-t border-amber-500/10">
        <div className="px-2 py-2 flex items-center justify-between gap-2">
          <div className="text-xs text-slate-400 min-w-0 truncate">{user?.first_name} {user?.last_name} · <span className="text-amber-400">Teamleiter</span></div>
          <ContactVisibilityToggle />
        </div>

      <button onClick={logout} className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm text-slate-300 hover:bg-white/5"><LogOut className="w-4 h-4" />Abmelden</button>
      </div>
    </div>
  );
}

export function TeamLeaderShell({ children }) {
  const mobileDesign = useMobileDesign();
  const liquid = useLiquidGold();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const { user } = useAuth();
  const back = useSafeBack(user);
  const loc = useLocation();
  const unread = useUnread();
  if(liquid)return <LiquidShell unread={unread} mode="team" extraSections={TEAM_NAV} tools={<><UndoButton/><ContactVisibilityToggle/></>}>{children}</LiquidShell>;
  if(mobileDesign) return <OpportunityScene unread={unread} mode="team" extraSections={TEAM_NAV} tools={<><UndoButton/><ContactVisibilityToggle/></>}>{children}</OpportunityScene>;
  return (
    <div className={cx("app-bg mobile-premium cockpit-shell app-mobile-console console-compact min-h-screen lg:flex pb-20 lg:pb-0", loc.pathname === "/m/career" && "career-shell career-team-shell")}>
      <DesktopCockpitHeader unread={unread} extraSections={TEAM_NAV} mode="team" tools={<><UndoButton /><ContactVisibilityToggle /></>} />
      <aside style={{ top: "var(--employee-preview-height, 0px)" }} className="cockpit-legacy-sidebar hidden lg:block w-64 shrink-0 border-r border-amber-500/10 bg-[#070B16]/60 h-screen sticky top-0"><TeamSidebarContent /></aside>
      <div className="flex-1 min-w-0">
        <header style={{ top: "var(--employee-preview-height, 0px)" }} className="cockpit-legacy-header lg:hidden sticky top-0 z-30 backdrop-blur-md bg-[#050811]/85 border-b border-amber-500/10 h-14 px-4 flex items-center justify-between">
          <div className="flex items-center gap-2"><button onClick={back} aria-label="Zurück" className="w-9 h-9 rounded-xl border border-amber-500/20 flex items-center justify-center text-amber-300"><ChevronLeft className="w-5 h-5" /></button><BrandHome compact /></div>
          <div className="flex items-center gap-2">
            <button onClick={() => nav("/m/notifications")} aria-label="Benachrichtigungen öffnen" className="relative w-10 h-10 rounded-xl border border-amber-500/20 flex items-center justify-center text-amber-300">
              <Bell className="w-5 h-5" />{unread > 0 && <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center">{unread}</span>}
            </button>
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild><button aria-label="Menü öffnen" className="w-10 h-10 rounded-xl border border-amber-500/20 flex items-center justify-center text-amber-300"><Menu className="w-5 h-5" /></button></SheetTrigger>
              <SheetContent side="right" aria-describedby={undefined} className="p-0 w-72 bg-[#070B16] border-amber-500/15"><SheetTitle className="sr-only">Menü</SheetTitle><TeamSidebarContent onNav={() => setOpen(false)} /></SheetContent>
            </Sheet>
          </div>
        </header>
        <GlobalXPIndicator />
        <div className="cockpit-legacy-actions hidden lg:flex justify-end px-6 pt-4"><DesktopHomeActions /></div>
        <main key={loc.pathname} className={cx(loc.pathname === "/m/career" ? "career-full-width" : "max-w-6xl", "mx-auto px-4 sm:px-6 py-6 page-enter")}>{children}</main>
      </div>
      <MobileSalesConsole />
    </div>
  );
}


function SidebarContent({ onNav }) {
  const { user, logout, exitAdminMode } = useAuth();
  const nav = useNavigate();
  const unread = useUnread();
  const leaveAdminMode = async () => {
    try {
      const normal = await exitAdminMode();
      onNav?.();
      nav(normal?.base_role === "vertriebspartner" ? "/m/my-contacts" : "/m/home", { replace: true });
      toast.success("Admin-Modus beendet.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Admin-Modus konnte nicht beendet werden.");
    }
  };
  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="px-5 py-5 border-b border-amber-500/10 flex items-center justify-between gap-2"><BrandHome to="/admin/dashboard" onClick={onNav} /><UndoButton /></div>
      {user?.admin_mode && (
        <div className="mx-3 mt-3 rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2" data-testid="admin-mode-indicator">
          <div className="text-xs font-semibold text-amber-300">Admin-Modus · {user?.first_name} {user?.last_name}</div>
          <button onClick={leaveAdminMode} className="mt-1 text-xs text-slate-200 underline underline-offset-2" data-testid="admin-mode-exit">Admin-Modus verlassen</button>
        </div>
      )}
      <div className="flex-1 overflow-y-auto px-3 py-4 space-y-5">
        {ADMIN_MENU.map((sec) => (
          <div key={sec.section}>
            <div className="px-2 mb-2 text-[10px] uppercase tracking-wider text-amber-400/60 font-semibold">{sec.section}</div>
            <div className="space-y-0.5">
              {sec.items.filter((i) => (!i.prewarmOnly || canPrewarm(user)) && (!i.janOnly || (user?.email || "").trim().toLowerCase() === "jan.nesic@loewen-stark.net") && (!i.adminOnly || user?.role === "admin") && (user?.role === "admin" || !["/admin/routing","/admin/mails","/admin/settings","/admin/import","/admin/historical-import","/admin/teams","/admin/guides","/admin/audit-log","/admin/login-log"].includes(i.to)))
                .map((i) => (
                <AreaNavLink key={i.to} to={i.to} onClick={onNav} data-testid={`admin-nav-${i.label.toLowerCase().replace(/[^a-z]/g,'-')}`}
                  className={({ isActive }) => cx("flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition tap",
                    isActive ? "nav-bar-active bg-amber-500/12 text-amber-200" : "text-slate-400 hover:text-white hover:bg-white/5")}>
                  <i.icon className="w-4.5 h-4.5 w-4 h-4" />{i.label}
                  {i.to === "/admin/notifications" && unread > 0 && (
                    <span className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center">{unread}</span>
                  )}
                </AreaNavLink>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="p-3 border-t border-amber-500/10">
        <div className="px-2 py-2 flex items-center justify-between gap-2">
          <div className="text-xs text-slate-400 min-w-0 truncate">{user?.first_name} {user?.last_name} · <span className="text-amber-400 capitalize">{user?.admin_mode ? "Admin-Modus" : user?.role}</span></div>
          <ContactVisibilityToggle />
        </div>

      <button onClick={logout} data-testid="logout-button" className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm text-slate-300 hover:bg-white/5">
          <LogOut className="w-4 h-4" /> Abmelden
        </button>
      </div>
    </div>
  );
}



export function AdminShell({ children }) {
  const mobileDesign = useMobileDesign();
  const liquid = useLiquidGold();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const { user } = useAuth();
  const back = useSafeBack(user);
  const loc = useLocation();
  const unread = useUnread();
  const showBack = loc.pathname !== "/admin/dashboard";
  if(liquid)return <LiquidShell unread={unread} mode="admin" extraSections={ADMIN_MENU} tools={<><UndoButton/><ContactVisibilityToggle/></>}>{children}</LiquidShell>;
  if(mobileDesign) return <OpportunityScene unread={unread} mode="admin" extraSections={ADMIN_MENU} tools={<><UndoButton/><ContactVisibilityToggle/></>}>{children}</OpportunityScene>;
  return (
    <div className="app-bg mobile-premium cockpit-shell min-h-screen lg:flex">
      <DesktopCockpitHeader unread={unread} extraSections={ADMIN_MENU} mode="admin" tools={<><UndoButton /><ContactVisibilityToggle /></>} />
      <aside style={{ top: "var(--employee-preview-height, 0px)" }} className="cockpit-legacy-sidebar hidden lg:block w-64 shrink-0 border-r border-amber-500/10 bg-[#070B16]/60 h-screen sticky top-0">
        <SidebarContent />
      </aside>
      <div className="flex-1 min-w-0">
        <header style={{ top: "var(--employee-preview-height, 0px)" }} className="cockpit-legacy-header lg:hidden sticky top-0 z-30 backdrop-blur-md bg-[#050811]/85 border-b border-amber-500/10 h-14 px-4 flex items-center justify-between">
          <BrandHome compact to="/admin/dashboard" />
          <div className="flex items-center gap-2">
            <UndoButton />
            <ContactVisibilityToggle />
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button aria-label="Menü öffnen" data-testid="admin-menu-toggle" className="w-10 h-10 rounded-xl border border-amber-500/20 flex items-center justify-center text-amber-300"><Menu className="w-5 h-5" /></button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-72 bg-[#070B16] border-amber-500/15" aria-describedby={undefined}>
                <SheetTitle className="sr-only">Menü</SheetTitle>
                <SidebarContent onNav={() => setOpen(false)} />
              </SheetContent>
            </Sheet>
          </div>
        </header>
        <div className="cockpit-legacy-actions hidden lg:flex justify-end px-6 pt-4"><DesktopHomeActions to="/admin/dashboard" /></div>
        <main key={loc.pathname} className="max-w-6xl mx-auto px-4 sm:px-6 py-6 page-enter">
          {showBack && (
            <button onClick={back} data-testid="admin-back" className="mb-4 inline-flex items-center gap-1 text-sm text-amber-300 hover:text-amber-200"><ChevronLeft className="w-4 h-4" />Zurück</button>
          )}
          {children}
        </main>
      </div>
    </div>
  );
}






